import { Component, inject, Input, OnInit } from '@angular/core';
import {
  CadastroDisparoAutomatizadoComponent
} from './cadastro-disparo-automatizado/cadastro-disparo-automatizado.component';
import {
  TabelaDisparoAutomaticoComponent
} from './cadastro-disparo-automatizado/utils/tabela-disparo-automatico/tabela-disparo-automatico.component';
import {DisparoAutomaticoModel} from '../shared/model/disparo-automatico/disparo-automatico.model';
import {MethodsUtils} from '../shared/util/methods.utils';
import { Ambiente } from '../shared/enum/ambiente.enum';
import { environment } from '../../../environments/environment';
import {TipoProgramacaoDisparo} from './cadastro-disparo-automatizado/utils/programacao-disparo-form/programacao-disparo-form.component';
import {ProgramacaoDisparoModel} from '../shared/model/disparo-automatico/programacao-disparo.model';
import {FILTRO_RECORRENCIA_SESSION_KEY} from '../shared/model/disparo-automatico/disparo-automatico-filtro.model';
import { ActivatedRoute, Router } from '@angular/router';
import { DisparoAutomaticoService } from '../shared/service/disparo-automatico.service';
import { ProgramacaoDisparoService } from '../shared/service/programacao-disparo.service';
import { firstValueFrom } from 'rxjs';
import { MsgService } from '../shared/service/msg.service';

@Component({
  selector: 'app-disparo-automatizado',
  standalone: true,
  imports: [
    CadastroDisparoAutomatizadoComponent,
    TabelaDisparoAutomaticoComponent
  ],
  templateUrl: './disparo-automatizado.component.html',
  styleUrl: './disparo-automatizado.component.css'
})
export class DisparoAutomatizadoComponent implements OnInit {
  @Input() statusGroup: 'DISPAROS' | 'PROGRAMACOES' = 'DISPAROS';
  @Input() tipoProgramacaoInicial: TipoProgramacaoDisparo = 'Padrão';

  adicaoDisparo = false;
  disparoAutomatico: DisparoAutomaticoModel = new DisparoAutomaticoModel();
  programacaoId?: number;
  programacaoSelecionada?: ProgramacaoDisparoModel;
  carregandoCadastro = false;

  ehMobile = MethodsUtils.detectar_mobile() || MethodsUtils.resolucaoPolegadas() < 13;
  isHml: boolean = environment.ambiente != Ambiente.PROD;

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly disparoService = inject(DisparoAutomaticoService);
  private readonly programacaoService = inject(ProgramacaoDisparoService);
  private readonly msgService = inject(MsgService);

  async ngOnInit(): Promise<void> {
    if (this.route.snapshot.routeConfig?.path?.endsWith('/novo')) {
      const estado = history.state as { replicar?: boolean; id?: number; ehProgramacao?: boolean };
      if (estado?.replicar && estado.id) {
        this.adicaoDisparo = true;
        this.carregandoCadastro = true;
        try {
          await this.abrirReplicaPorId(estado.id, estado.ehProgramacao === true);
        } catch {
          this.msgService.msgErro('Não foi possível carregar os dados para replicar o disparo.');
          await this.router.navigate([this.rotaBase()]);
        } finally {
          this.carregandoCadastro = false;
        }
      } else {
        this.abrirAdicaoLocal();
      }
      return;
    }

    this.route.queryParamMap.subscribe(parametros => {
      const uuid = parametros.get('uuid');
      if (uuid) {
        const estado = history.state as { ehProgramacao?: boolean };

        this.adicaoDisparo = true;
        this.carregandoCadastro = true;
        this.abrirPorUuid(uuid, estado?.ehProgramacao)
          .catch(() => this.msgService.msgErro('Não foi possível carregar os dados do disparo.'))
          .finally(() => this.carregandoCadastro = false);
      } else {
        this.exibirTabela();
      }
    });
  }

  abrirAdicao(){
    this.router.navigate([this.rotaBase(), 'novo']);
  }

  private abrirAdicaoLocal(){
    this.disparoAutomatico = new DisparoAutomaticoModel();
    this.programacaoSelecionada = undefined;
    this.adicaoDisparo = true;
  }

  disparoAutomaticoSelecionado(event: any){
    const ehProgramacao = event.ehProgramacaoRecorrente === true;
    if (event.replicar) {
      this.adicaoDisparo = true;
      this.carregandoCadastro = true;
      this.router.navigate([this.rotaBase(), 'novo'], {
        state: {
          replicar: true,
          id: ehProgramacao ? (event.programacaoId ?? event.id) : event.id,
          ehProgramacao
        }
      });
      return;
    }

    const uuid = event.programacao?.uuid ?? event.uuid;
    if (!uuid) return;
    this.router.navigate([this.rotaBase()], {
      queryParams: { uuid },
      state: { ehProgramacao }
    });
  }

  private async abrirReplicaPorId(id: number, ehProgramacao: boolean): Promise<void> {
    if (ehProgramacao) {
      const programacao = await firstValueFrom(this.programacaoService.buscarProgramacao(id));
      this.programacaoSelecionada = this.prepararReplicaProgramacao(programacao);
      this.disparoAutomatico = this.programacaoSelecionada.disparo ?? new DisparoAutomaticoModel();
      this.disparoAutomatico.ehProgramacaoRecorrente = true;
      this.adicaoDisparo = true;
      return;
    }

    const disparo = await firstValueFrom(this.disparoService.buscarDisparo(id));
    if (disparo.origemContatos !== 'BASE_DINAMICA') {
      disparo.origemContatos = 'EXCEL';
      disparo.listaContatos = await this.buscarTodosContatos(id);
    }
    this.disparoAutomatico = this.prepararReplicaDisparo(disparo);
    this.programacaoSelecionada = undefined;
    this.adicaoDisparo = true;
  }

  private async abrirPorUuid(uuid: string, ehProgramacao?: boolean): Promise<void> {
    if (this.statusGroup === 'PROGRAMACOES' && ehProgramacao !== false) {
      try {
        const programacao = await firstValueFrom(this.programacaoService.buscarProgramacaoPorUuid(uuid));
        this.programacaoSelecionada = programacao;
        this.disparoAutomatico = programacao.disparo ?? new DisparoAutomaticoModel();
        this.disparoAutomatico.ehProgramacaoRecorrente = true;
        this.adicaoDisparo = true;
        return;
      } catch (erro) {
        if (ehProgramacao === true) throw erro;
      }
    }

    this.disparoAutomatico = await firstValueFrom(this.disparoService.buscarDisparoPorUuid(uuid));
    this.programacaoSelecionada = undefined;
    this.adicaoDisparo = true;
  }

  private exibirTabela(): void {
    this.adicaoDisparo = false;
    this.disparoAutomatico = new DisparoAutomaticoModel();
    this.programacaoSelecionada = undefined;
    if (this.statusGroup !== 'DISPAROS') return;

    const valorTemporario = sessionStorage.getItem(FILTRO_RECORRENCIA_SESSION_KEY);
    sessionStorage.removeItem(FILTRO_RECORRENCIA_SESSION_KEY);
    const programacaoId = Number(valorTemporario);
    this.programacaoId = Number.isInteger(programacaoId) && programacaoId > 0 ? programacaoId : undefined;
  }

  private async buscarTodosContatos(disparoId: number): Promise<any[]> {
    const contatos: any[] = [];
    let pagina = 0;
    while (true) {
      const resposta: any = await firstValueFrom(
        this.disparoService.buscarContatosPorDisparo(disparoId, pagina, 200)
      );
      contatos.push(...(resposta.content ?? []));
      if (pagina + 1 >= (resposta.totalPages ?? 0)) break;
      pagina++;
    }
    return contatos;
  }

  private prepararReplicaDisparo(origem: DisparoAutomaticoModel): DisparoAutomaticoModel {
    const replica = Object.assign(new DisparoAutomaticoModel(), structuredClone(origem));
    replica.id = undefined;
    replica.uuid = undefined;
    replica.programacaoId = undefined;
    replica.programacao = undefined;
    replica.ehProgramacaoRecorrente = false;
    replica.status = undefined;
    replica.dataEnvio = undefined;
    replica.usuarioEnvio = undefined;
    replica.setor = undefined;
    replica.infos = undefined;
    replica.custoDisparo = undefined;
    replica.investimento = undefined;
    replica.possuiFalhaIntegracao = false;
    replica.quantidadeContatosValidos = 0;
    replica.listaContatos = (replica.listaContatos ?? []).map((contato: any) => ({
      ...contato,
      id: undefined,
      disparoAutomatico: undefined,
      status: undefined,
      statusEnvio: undefined,
      statusEnvioIntegracao: undefined,
      motivoErro: undefined,
      mensagemId: undefined,
      dataEnvio: undefined,
      camposComplementares: (contato.camposComplementares ?? []).map((campo: any) => ({
        ...campo,
        id: undefined,
        disparoContatoId: undefined,
        disparoContato: undefined
      }))
    }));
    replica.colunasComplementares = (replica.colunasComplementares ?? []).map(coluna => ({
      ...coluna,
      id: undefined,
      valores: (coluna.valores ?? []).map(valor => ({
        ...valor,
        disparoContatoId: undefined as unknown as number
      }))
    }));
    return replica;
  }

  private prepararReplicaProgramacao(origem: ProgramacaoDisparoModel): ProgramacaoDisparoModel {
    const replica = Object.assign(new ProgramacaoDisparoModel(), structuredClone(origem));
    replica.id = undefined;
    replica.uuid = undefined;
    replica.programacaoId = undefined;
    replica.disparoId = undefined;
    replica.ativo = undefined;
    replica.status = undefined;
    replica.dataCriacao = undefined;
    replica.enviosRealizados = 0;
    replica.proximaExecucao = undefined;
    replica.quantidadeEnvios = undefined;
    replica.quantidadeErros = undefined;
    replica.investimentoAcumulado = undefined;
    replica.disparo = this.prepararReplicaDisparo(replica.disparo ?? new DisparoAutomaticoModel());
    replica.disparo.ehProgramacaoRecorrente = true;
    return replica;
  }

  fecharCadastro(): void {
    this.router.navigate([this.rotaBase()]);
  }

  private rotaBase(): string {
    return this.statusGroup === 'PROGRAMACOES' ? '/programacoes' : '/disparos';
  }

  gerarAlturaComponent(): string {
    if (this.isHml) {
      return 'calc(var(--altura-util-pagina) - 76px)';
    } else {
      return 'calc(var(--altura-util-pagina) - 62px)';
    }
  }
}
